import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, Clock, Truck, ArrowsClockwise, TrendUp, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { Eyebrow } from "@/components/marketing/Primitives";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const WEEKS = 46;         // working weeks / year
const LABOUR_RATE = 25;   // £ / admin hour

const fields = [
  { key: "engineers", label: "Field engineers", min: 1, max: 100, step: 1, def: 10, fmt: (v) => `${v}` },
  { key: "jobs", label: "Jobs per engineer / week", min: 1, max: 40, step: 1, def: 15, fmt: (v) => `${v}` },
  { key: "admin", label: "Admin hours per engineer / week", min: 1, max: 20, step: 1, def: 6, fmt: (v) => `${v} hrs` },
  { key: "profit", label: "Average profit per job", min: 20, max: 1000, step: 10, def: 120, fmt: (v) => `£${v}` },
];

const gbp = (n) => `£${Math.round(n).toLocaleString()}`;

export function LeanCalculator() {
  const [vals, setVals] = useState(Object.fromEntries(fields.map((f) => [f.key, f.def])));
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (arr) => setVals((v) => ({ ...v, [key]: arr[0] }));

  const result = useMemo(() => {
    const { engineers, jobs, admin, profit } = vals;
    const adminSavings = engineers * admin * 0.3 * WEEKS * LABOUR_RATE; // 30% admin cut
    const extraJobs = engineers * jobs * WEEKS * 0.1;                   // +10% capacity
    const jobRevenue = extraJobs * profit;
    const travel = engineers * 800;                                     // less wasted travel/fuel
    const misc = engineers * 500;                                       // fewer repeat visits / lost stock
    const total = adminSavings + jobRevenue + travel + misc;
    return {
      total,
      breakdown: [
        { icon: Clock, label: "Admin time recovered", value: adminSavings },
        { icon: TrendUp, label: "Extra jobs completed", value: jobRevenue },
        { icon: Truck, label: "Reduced travel & fuel", value: travel },
        { icon: ArrowsClockwise, label: "Fewer repeat visits & lost stock", value: misc },
      ],
    };
  }, [vals]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email.");
    setSending(true);
    try {
      const summary =
        `Lean Calculator estimate: ${gbp(result.total)}/yr potential savings. ` +
        `Inputs — ${vals.engineers} engineers, ${vals.jobs} jobs/eng/week, ${vals.admin} admin hrs/eng/week, £${vals.profit} profit/job.`;
      await api.post("/enquiries", {
        name: company || "Lean Calculator lead",
        company,
        email,
        telephone: "",
        business_size: `${vals.engineers} engineers`,
        message: summary,
        enquiry_type: "calculator",
      });
      setSent(true);
      toast.success("Your savings report is on its way!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow className="mx-auto">Savings calculator</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
              How much are you losing?
            </h2>
            <p className="mt-4 text-muted-foreground md:text-lg">
              Move the sliders to match your business and see how much a leaner operation could put back on your bottom line each year.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Inputs */}
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-card p-8">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Calculator size={22} weight="duotone" />
                </span>
                <h3 className="font-heading text-lg font-semibold text-white">Your business</h3>
              </div>
              <div className="mt-8 space-y-8">
                {fields.map((f) => (
                  <div key={f.key}>
                    <div className="mb-3 flex items-center justify-between">
                      <Label className="text-sm text-muted-foreground">{f.label}</Label>
                      <span className="font-heading text-sm font-bold text-primary">{f.fmt(vals[f.key])}</span>
                    </div>
                    <Slider
                      value={[vals[f.key]]}
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      onValueChange={set(f.key)}
                      data-testid={`calc-${f.key}`}
                      aria-label={f.label}
                    />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Result */}
          <Reveal delay={0.1}>
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-primary/40 bg-primary/[0.06] p-8 glow-green">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/15 blur-3xl" />
              <p className="text-sm font-semibold uppercase tracking-wide text-primary">Estimated annual saving</p>
              <p className="mt-2 font-heading text-5xl font-extrabold text-white sm:text-6xl" data-testid="calc-total">
                <Counter key={Math.round(result.total)} value={Math.round(result.total)} prefix="£" duration={900} />
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Potential recoverable waste per year with FlowCore+</p>

              <div className="mt-6 space-y-3">
                {result.breakdown.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.label} className="flex items-center justify-between rounded-xl bg-background/60 px-4 py-3">
                      <span className="flex items-center gap-2.5 text-sm text-white">
                        <Icon size={18} weight="duotone" className="text-primary" /> {b.label}
                      </span>
                      <span className="font-heading text-sm font-bold text-white">{gbp(b.value)}</span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-auto pt-6">
                <AnimatePresence mode="wait">
                  {sent ? (
                    <motion.div
                      key="done"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl border border-white/10 bg-background/70 p-5 text-center"
                      data-testid="calc-success"
                    >
                      <CheckCircle size={40} weight="fill" className="mx-auto text-primary" />
                      <p className="mt-2 font-semibold text-white">Your report is on its way</p>
                      <p className="mt-1 text-sm text-muted-foreground">We'll be in touch to show you how to unlock these savings.</p>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      onSubmit={submit}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="rounded-2xl border border-white/10 bg-background/60 p-5"
                      data-testid="calc-form"
                    >
                      <p className="text-sm font-semibold text-white">Get your detailed savings breakdown</p>
                      <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company (optional)" className="bg-card" data-testid="calc-company" />
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email *" className="bg-card" data-testid="calc-email" required />
                      </div>
                      <button
                        type="submit"
                        disabled={sending}
                        className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60"
                        data-testid="calc-submit"
                      >
                        {sending ? "Sending…" : (<><PaperPlaneTilt size={16} weight="bold" /> Email me my savings report</>)}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </Reveal>
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Estimates are indicative and based on typical FlowCore+ efficiency gains. Book a demo for a tailored analysis.
        </p>
      </div>
    </section>
  );
}
